import { useEffect, useRef, useState, type RefObject } from "react";
import { ArrowUp } from "lucide-react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { useAuthStore } from "../../store/auth.store";

// Sekme/sayfa basligi: route'a gore document.title ayarla
const TITLE_MAP: Array<[RegExp, string]> = [
  [/^\/analyses\/new/, "Yeni Analiz"],
  [/^\/analyses\/[^/]+/, "Analiz Detayı"],
  [/^\/analyses/, "Ürün Analizleri"],
  [/^\/listing-builder/, "Listing Builder"],
  [/^\/listing-drafts/, "Listing Taslakları"],
  [/^\/mockup-studio/, "Mockup Studio"],
  [/^\/mockup-manager/, "Mockup Yönetimi"],
  [/^\/designs/, "Tasarımlar"],
  [/^\/templates/, "Şablonlar"],
  [/^\/supplier-catalogs/, "Tedarikçi Katalog"],
  [/^\/my-shops/, "Mağazalarım"],
  [/^\/shops\/[^/]+/, "Mağaza Detayı"],
  [/^\/shops/, "Mağaza Takip"],
  [/^\/scheduler/, "Zamanlayıcı"],
  [/^\/concepts/, "Konseptler"],
  [/^\/trend-intelligence/, "Trend Intelligence"],
  [/^\/ai-usage/, "AI Kullanımı"],
  [/^\/chatgpt/, "ChatGPT / MCP"],
  [/^\/listing-review/, "Liste Denetimi"],
  [/^\/sales-dashboard/, "Satış Raporu"],
  [/^\/finance/, "İstatistikler"],
  [/^\/stats/, "İstatistikler"],
  [/^\/inventory/, "Envanter"],
  [/^\/pricing/, "Fiyat Asistanı"],
  [/^\/bulk-operations/, "Toplu İşlem"],
  [/^\/design-builder/, "Design Builder"],
  [/^\/etsy\/products/, "Etsy Ürünlerim"],
  [/^\/etsy\/orders/, "Siparişler"],
  [/^\/etsy\/reviews/, "Yorumlar"],
  [/^\/etsy\/messages/, "Alıcı Mesajları"],
  [/^\/design-engine/, "Tasarım Motoru"],
  [/^\/video-studio/, "Video Studio"],
  [/^\/erank/, "eRank Analiz"],
  [/^\/discovery/, "Ürün Keşfi"],
  [/^\/tasks/, "Eklenti Görevleri"],
  [/^\/pinterest/, "Pinterest"],
  [/^\/plans/, "Planlarım"],
  [/^\/settings/, "Ayarlar"],
];

function useDocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const found = TITLE_MAP.find(([re]) => re.test(pathname));
    const name = found ? found[1] : (pathname === "/" || pathname === "/dashboard" ? "Dashboard" : "");
    document.title = name ? `${name} · Etsy Research Copilot` : "Etsy Research Copilot";
  }, [pathname]);
}

/** Uzun sayfalarda asagi inilince sag altta "en uste cik" dugmesi (icerik main icinde kayar) */
function ScrollTopButton({ target }: { target: RefObject<HTMLElement> }) {
  const [show, setShow] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const el = target.current;
    if (!el) return undefined;
    const onScroll = () => setShow(el.scrollTop > 600);
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [target, pathname]);
  if (!show) return null;
  return (
    <button type="button" onClick={() => target.current?.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="En üste çık" title="En üste çık"
      className="fixed bottom-6 right-6 z-40 rounded-full border border-gray-200 bg-white p-3 text-gray-700 shadow-md hover:bg-gray-50 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-200">
      <ArrowUp className="h-5 w-5" />
    </button>
  );
}

/** Panel acikken sayfanin kendisi (html/body) kaymaz: yalniz <main> kayar. Govdeyi uzatan bir
 *  oge (gizli girdi, tasan katman) olunca ikinci kaydirma cubugu cikip tum panel yukari kayiyordu. */
function useLockDocumentScroll() {
  useEffect(() => {
    const html = document.documentElement, body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = "hidden"; body.style.overflow = "hidden";
    const reset = () => { if (window.scrollY || window.scrollX) window.scrollTo(0, 0); };
    reset();
    window.addEventListener("scroll", reset, { passive: true });
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; window.removeEventListener("scroll", reset); };
  }, []);
}

export function AppLayout() {
  const { isAuthenticated } = useAuthStore();
  const mainRef = useRef<HTMLElement>(null);
  useDocumentTitle();
  useLockDocumentScroll();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  const pinTop = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && (e.currentTarget.scrollTop || e.currentTarget.scrollLeft)) { e.currentTarget.scrollTop = 0; e.currentTarget.scrollLeft = 0; }
  };

  return (
    // overflow-hidden kutular da odak/scrollIntoView ile kayabiliyor: icerik yukari kayip altta
    // bos alan kaliyordu (Custom Studyo 5. adim). Yalniz <main> kayar; disaridakiler hep basta.
    <div className="flex h-screen overflow-hidden bg-gray-50" onScroll={pinTop}>
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden" onScroll={pinTop}>
        <Header />
        <main ref={mainRef} className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
        <ScrollTopButton target={mainRef} />
      </div>
    </div>
  );
}
